import { useCallback, useEffect, useState } from "react";
import type { Key } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { PanelDetail } from "../../types";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";

export function PanelPanelsTable({ panel }: { panel: PanelDetail }) {
  const subpanels = panel.panels || [];

  const { options } = useAdminGridOptions(
    subpanels,
    [
      {
        field: "name",
        cellRenderer: (params) => (
          <Link to="/admin/panels/$id/edit" params={{ id: params.id }}>
            {params.name}
          </Link>
        ),
        cellRendererParams: (params) => ({
          id: params.data.id,
          name: params.data.name,
        }),
      },
    ],
    { sideBar: undefined },
  );

  return (
    <TabLayout
      description="Sub-panels included in this panel"
      actions={<UpdatePanelsDialog panel={panel} />}
    >
      <div className="flex h-96">
        <AdminGrid {...options} />
      </div>
    </TabLayout>
  );
}

function UpdatePanelsDialog({ panel }: { panel: PanelDetail }) {
  const [open, setOpen] = useState(false);
  const [keys, setKeys] = useState<Key[]>(panel.panels.map((t) => t.id));
  const { data, isLoading } = trpc.panels.all.useQuery();

  const updatePanels = trpc.admin.panel.updateChildPanels.useMutation();

  useEffect(() => {
    if (open) {
      setKeys(panel.panels.map((t) => t.id));
    }
  }, [panel.panels, open]);

  const onSelectionChange = useCallback(
    (keys: "all" | Set<Key>) => {
      if (keys === "all") {
        setKeys(data?.map((t) => t.id) || []);
      } else {
        setKeys(Array.from(keys));
      }
    },
    [data],
  );

  const onUpdate = useCallback(() => {
    updatePanels.mutate(
      { id: panel.id, panelIds: keys.map((k) => Number(k)) },
      mutationResponse({
        errorTitle: "Failed to update sub-panels",
        successMessage: "Sub-panels updated",
        onSuccess: () => setOpen(false),
      }),
    );
  }, [keys, panel.id, updatePanels]);

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button variant="secondary" onPress={() => setOpen(true)}>
        Edit sub-panels
      </Button>
      <Modal isOpen={open} onOpenChange={setOpen}>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Update child panels</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <GridList
              items={data || []}
              selectionMode="multiple"
              selectedKeys={keys}
              onSelectionChange={onSelectionChange}
              // isLoading={isLoading}
            >
              {(panel) => (
                <GridListItem key={panel.id} textValue={panel.name}>
                  <Text>{panel.name}</Text>
                </GridListItem>
              )}
            </GridList>
          </DialogBody>
          <DialogFooter>
            <Button isPending={updatePanels.isPending} onPress={onUpdate}>
              Update
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
